import { AnimatePresence, motion } from 'framer-motion';
import QRCode from 'react-qr-code';
import { VideoPlayer } from '@/components/VideoPlayer/VideoPlayer';
import { ImageGallery } from '@/components/ImageGallery/ImageGallery';
import type { ItemDetail, LanguageCode } from '@/types';

interface ItemModalProps {
  item: ItemDetail | null | undefined;
  isLoading: boolean;
  lang: LanguageCode;
  onClose: () => void;
  qrHint: string;
}

const LOCALE_MAP: Record<LanguageCode, string> = { hu: 'hu-HU', en: 'en-GB', de: 'de-DE' };

/** Az esemény kezdő/záró dátumát formázza — egésznapos eseménynél idő nélkül, egyébként idővel. */
function formatEventRange(
  eventStart: string | null,
  eventEnd: string | null,
  isAllDay: boolean,
  lang: LanguageCode,
): string | null {
  if (!eventStart) return null;

  const locale = LOCALE_MAP[lang] ?? 'hu-HU';
  const start = new Date(eventStart.replace(' ', 'T'));
  const end = eventEnd ? new Date(eventEnd.replace(' ', 'T')) : null;

  const dateFmt = new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'long', day: 'numeric' });
  const timeFmt = new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' });

  const sameDay = end ? start.toDateString() === end.toDateString() : true;

  if (isAllDay) {
    if (!end || sameDay) return dateFmt.format(start);
    return `${dateFmt.format(start)} – ${dateFmt.format(end)}`;
  }

  if (!end) return `${dateFmt.format(start)} ${timeFmt.format(start)}`;
  if (sameDay) return `${dateFmt.format(start)} ${timeFmt.format(start)}–${timeFmt.format(end)}`;
  return `${dateFmt.format(start)} ${timeFmt.format(start)} – ${dateFmt.format(end)} ${timeFmt.format(end)}`;
}

export function ItemModal({ item, isLoading, lang, onClose, qrHint }: ItemModalProps) {
  const isOpen = Boolean(item) || isLoading;
  // A QR-kód a backenden megadott weboldal-linkből készül — ha nincs weboldal megadva az elemhez,
  // nincs mit QR-kóddá alakítani, ezért a blokk egyáltalán nem jelenik meg.
  const websiteUrl = item?.website?.trim() || null;
  const eventRange = item
    ? formatEventRange(item.event_start, item.event_end, Boolean(item.is_all_day), lang)
    : null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-kiosk-overlay flex items-center justify-center p-4 kiosk:p-0"
          onClick={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 12 }}
            transition={{ type: 'spring', stiffness: 300, damping: 28 }}
            className="relative bg-kiosk-panel border border-kiosk-border rounded-2xl kiosk:rounded-kiosk w-full max-w-lg kiosk:w-[84vw] kiosk:max-w-none max-h-[85vh] overflow-y-auto p-5 kiosk:p-[3vw] flex flex-col kiosk:flex-row gap-4 kiosk:gap-[3vw] shadow-2xl shadow-black/10"
          >
            <button
              type="button"
              onClick={onClose}
              className="absolute top-2 right-2 kiosk:top-[1.6vw] kiosk:right-[1.6vw] z-10 w-10 h-10 kiosk:w-[3.6vw] kiosk:h-[3.6vw] flex items-center justify-center rounded-full bg-kiosk-bg border border-kiosk-border text-2xl kiosk:text-kiosk-xl leading-none text-kiosk-muted hover:text-kiosk-accent hover:border-kiosk-accent transition-colors"
              aria-label="Bezárás"
            >
              &times;
            </button>

            {item && (
              <>
                <div className="flex-1 flex flex-col gap-3 kiosk:gap-[1.4vw]">
                  <h2 className="font-display text-kiosk-xl font-bold pr-12 kiosk:pr-0">{item.title}</h2>
                  {Boolean(item.is_all_day) && (
                    <span className="self-start text-[0.65rem] kiosk:text-kiosk-xs font-semibold uppercase tracking-wide text-white bg-kiosk-accent2 px-2.5 py-1 kiosk:px-[1.2vw] kiosk:py-[0.5vw] rounded-full">
                      Egésznapos
                    </span>
                  )}
                  <div className="text-kiosk-xs text-kiosk-muted flex flex-col gap-1 kiosk:gap-[0.4vw]">
                    {eventRange && <span>📅 {eventRange}</span>}
                    {item.address && <span>📍 {item.address}</span>}
                    {item.opening_hours && <span>🕒 {item.opening_hours}</span>}
                    {item.phone && <span>📞 {item.phone}</span>}
                  </div>
                  {item.gallery.length > 0 && <ImageGallery images={item.gallery} />}
                  {item.video_path && <VideoPlayer src={item.video_path} />}
                  <p className="text-kiosk-sm leading-relaxed whitespace-pre-line">{item.description}</p>
                </div>

                {websiteUrl && (
                  <div className="flex-none kiosk:w-[22vw] text-center text-kiosk-xs text-kiosk-muted flex flex-col items-center gap-2 kiosk:gap-[1vw]">
                    <div className="bg-white p-3 rounded-xl kiosk:p-[1vw] kiosk:rounded-[1vw] border-2 border-kiosk-accent">
                      <QRCode value={websiteUrl} size={160} fgColor="#0a0a1e" />
                    </div>
                    <p>{qrHint}</p>
                  </div>
                )}
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
